/**
 * Company Secretary Import Template — parse, validate, duplicate detection
 */

export const COMPANY_SECRETARY_TEMPLATE_COLUMNS = [
  { key: 'firstName', label: 'First Name', required: true, example: 'Sara' },
  { key: 'lastName', label: 'Last Name', required: true, example: 'Al Neyadi' },
  { key: 'email', label: 'Email', required: true, example: 'secretary@company.example' },
  { key: 'phone', label: 'Phone', required: false, example: '+971 50 123 4567' },
  {
    key: 'assignedCompanies',
    label: 'Assigned Companies',
    required: false,
    example: 'Etisalat Group PJSC, Etisalat UAE',
    note: 'Optional (comma-separated company names)',
  },
  {
    key: 'sendWelcomeEmail',
    label: 'Send Welcome Email',
    required: false,
    example: 'Yes',
    note: 'Optional (Yes/No, default Yes)',
  },
  {
    key: 'mfaEmailOtp',
    label: 'MFA Email OTP',
    required: false,
    example: 'Yes',
    note: 'Optional (Yes/No, default Yes)',
  },
];

function escapeCsvCell(value) {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function generateCompanySecretaryTemplateData() {
  const headers = COMPANY_SECRETARY_TEMPLATE_COLUMNS.map((col) => col.label);
  const examples = COMPANY_SECRETARY_TEMPLATE_COLUMNS.map((col) => col.example);
  const notes = COMPANY_SECRETARY_TEMPLATE_COLUMNS.map((col) => {
    if (col.note) return col.note;
    return col.required ? 'Required' : 'Optional';
  });

  return { headers, examples, notes, columns: COMPANY_SECRETARY_TEMPLATE_COLUMNS };
}

export function downloadCompanySecretaryTemplate() {
  const { headers, examples, notes } = generateCompanySecretaryTemplateData();
  const csvContent = [
    headers.map(escapeCsvCell).join(','),
    examples.map(escapeCsvCell).join(','),
    notes.map(escapeCsvCell).join(','),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `company_secretary_import_template_${new Date().toISOString().split('T')[0]}.csv`
  );
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function safeString(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).trim();
}

function getCell(row, label, key) {
  return row[label] ?? row[key];
}

function parseYesNo(value, defaultValue = true) {
  const raw = safeString(value).toLowerCase();
  if (!raw) return defaultValue;
  if (['yes', 'y', 'true', '1'].includes(raw)) return true;
  if (['no', 'n', 'false', '0'].includes(raw)) return false;
  return defaultValue;
}

export function parseAssignedCompanyNames(value) {
  const raw = safeString(value);
  if (!raw) return [];
  return raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
}

export function parseImportedCompanySecretaryRow(row, index) {
  return {
    _rowIndex: index,
    firstName: safeString(getCell(row, 'First Name', 'firstName')),
    lastName: safeString(getCell(row, 'Last Name', 'lastName')),
    email: safeString(getCell(row, 'Email', 'email')).toLowerCase(),
    phone: safeString(getCell(row, 'Phone', 'phone')),
    assignedCompanyNames: parseAssignedCompanyNames(
      getCell(row, 'Assigned Companies', 'assignedCompanies')
    ),
    sendWelcomeEmail: parseYesNo(getCell(row, 'Send Welcome Email', 'sendWelcomeEmail'), true),
    mfaEmailOtp: parseYesNo(getCell(row, 'MFA Email OTP', 'mfaEmailOtp'), true),
  };
}

export function isEmptyCompanySecretaryRow(row) {
  return !row.firstName && !row.lastName && !row.email;
}

export function isCompanySecretaryTemplateHelperRow(row) {
  const firstName = safeString(row.firstName);
  if (/^(Required|Optional)(\s|$|\()/i.test(firstName)) {
    return true;
  }
  const { examples } = generateCompanySecretaryTemplateData();
  if (firstName === examples[0] && row.lastName === examples[1] && row.email === examples[2]) {
    return true;
  }
  return false;
}

export function validateImportedCompanySecretaryRow(row) {
  const errors = [];

  if (!row.firstName) errors.push('First Name is required');
  if (!row.lastName) errors.push('Last Name is required');
  if (!row.email) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
    errors.push('Invalid email format');
  }

  return { isValid: errors.length === 0, errors };
}

export function buildCompanySecretaryCreatePayload(secretary) {
  return {
    firstName: secretary.firstName,
    lastName: secretary.lastName,
    email: secretary.email,
    phone: secretary.phone || undefined,
    assignedCompanies: secretary.assignedCompanyIds || [],
    mfaEmailOtp: secretary.mfaEmailOtp !== false,
    sendWelcomeEmail: secretary.sendWelcomeEmail !== false,
  };
}

export function calculateCompanySecretarySimilarityScore(imported, existing) {
  let score = 0;
  let maxScore = 0;

  if (imported.email && existing.email) {
    maxScore += 70;
    if (imported.email.toLowerCase() === existing.email.toLowerCase()) {
      score += 70;
    }
  }

  const importedName = `${imported.firstName || ''} ${imported.lastName || ''}`.trim().toLowerCase();
  const existingName = (existing.name || '').trim().toLowerCase();
  if (importedName && existingName) {
    maxScore += 30;
    if (importedName === existingName) score += 30;
    else if (importedName.includes(existingName) || existingName.includes(importedName)) {
      score += 15;
    }
  }

  if (maxScore === 0) return 0;
  return Math.round((score / maxScore) * 100);
}

export { getMatchConfidenceLevel } from './companyImportTemplate';
